// packages/charts/src/chartTooltip.ts
// (moved here from apps/frontend/src/components/chat/message/chart/ on
//  2026-09-08 — plan one-drawing-layer, A2)
//
// Shared HTML-safety + tooltip-building helpers for chart renderers.
//
// ECharts renders tooltip formatter return values as raw HTML (`el.innerHTML`,
// TooltipHTMLContent.js), so every value interpolated into a formatter string is
// an injection surface. Any tenant-controlled string — product titles, series
// labels, image URLs — MUST be escaped before it reaches a formatter string.
// These helpers live in a small React/ECharts-free module so the formatter logic
// can be unit-tested directly, without instantiating a chart.

/**
 * Minimal HTML escape for values interpolated into tooltip innerHTML.
 * Formerly a module-private copy in MetricCardRenderer — extracted here so the
 * bar-tooltip formatter and the metric-card tooltip share one implementation.
 */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Return the normalized href of `url` iff it parses AND uses https, else null.
 *
 * Product image URLs originate from the warehouse (tenant-controlled), so they
 * are treated as untrusted: only https URLs render, and `URL.href` normalizes
 * (percent-encoding `"`/`<`/`>`) before the value reaches an attribute.
 */
export function safeHttpsUrl(url: unknown): string | null {
  if (typeof url !== "string" || url.length === 0) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" ? parsed.href : null;
  } catch {
    return null;
  }
}

/**
 * What a hovered row can reveal.
 *
 * `image` is the Shopify product thumbnail; `ad` is a reconstructed ad creative
 * (Meta / Google Ads). One union so table cells and bar tooltips have a single
 * resolve step, and adding a third kind stays a type error until every renderer
 * handles it.
 */
export type RowPreview =
  | { kind: "image"; imageUrl: string }
  | {
      kind: "ad";
      adType?: string | null;
      adName?: string | null;
      headlines: string[];
      descriptions: string[];
      /** Bare host, never a full URL — the backend reduces it (see the route). */
      displayUrl?: string | null;
      imageUrl?: string | null;
      youtubeVideoId?: string | null;
    };

/**
 * Resolve the hover preview for a chart/table row.
 *
 * Shared by the horizontal-bar tooltip and the dashboard tables. Two id columns
 * can key a preview and they are mutually exclusive in practice (a Shopify row
 * has no ad_id and vice versa):
 *
 *   * `product_id` — a positive integer, or a numeric STRING on composite
 *     tiles (their product_key column is STRING to also hold GA4 item ids).
 *     Custom/deleted line items yield NULL and are dropped.
 *   * `ad_id` — a numeric STRING in the ad marts. BigQuery hands numeric-looking
 *     ids back as strings, but a JSON round-trip elsewhere could still produce a
 *     number, so both are accepted and normalized with String().
 *
 * Everything else → null, which every caller renders as "no hover affordance".
 */
export function resolveRowPreview(
  row: Record<string, unknown> | undefined,
  previews: Record<string, RowPreview> | undefined,
): RowPreview | null {
  if (!row || !previews) return null;

  const pid = row["product_id"];
  if (typeof pid === "number" && Number.isInteger(pid) && pid > 0) {
    return previews[String(pid)] ?? null;
  }
  // The composite product mart carries product_key as a STRING (it must also
  // hold GA4 item ids), so Shopify ids arrive as numeric strings there.
  if (typeof pid === "string" && /^[1-9]\d{0,18}$/.test(pid)) {
    return previews[pid] ?? null;
  }

  const adId = row["ad_id"];
  if (typeof adId === "string" && adId.length > 0) return previews[adId] ?? null;
  if (typeof adId === "number" && Number.isInteger(adId) && adId > 0) {
    return previews[String(adId)] ?? null;
  }

  return null;
}

/**
 * The preview's image, iff it is a usable https URL.
 *
 * Both union members carry `imageUrl`, but they differ in whether it is
 * required: an `image` preview is nothing without it, while an `ad` preview is
 * mostly copy and often has none. Callers use that distinction — see IndexCell.
 */
export function previewImageUrl(preview: RowPreview | null): string | null {
  return preview ? safeHttpsUrl(preview.imageUrl) : null;
}

export type BarTooltipFormatterOptions = {
  /** Formats a numeric series value (e.g. currency / count) for display. */
  formatValue: (value: number) => string;
  /** row id → preview map (optional; progressive enhancement). */
  rowPreviews?: Record<string, RowPreview>;
  /**
   * When set, the tooltip groups each metric's two periods into one block
   * instead of listing them as separate series. Omit for single-series bars.
   */
  comparisonLabel?: string;
  /**
   * Name of the selected period ("Last Month"), used as the row label for the
   * current value. Falls back to "Selected period" when the caller has none.
   */
  periodLabel?: string;
};

/**
 * Build an ECharts axis-trigger tooltip formatter for horizontal-bar charts.
 *
 * Pure + ECharts-free: pass the SAME rows array that produced the category axis
 * (`yAxis.data`) and series data — the hovered row is resolved by
 * `params[0].dataIndex` into that array, never by category name. The category
 * axis is positional, so duplicate product titles (the exact collision this
 * feature exists for) would map a name-keyed lookup to the wrong row/image.
 *
 * Every interpolated value is escaped (ECharts renders the string via innerHTML).
 * When the resolved row maps to a preview, it is appended below the values.
 */
/** "prev. year" -> "Prev. year". Leaves an already-capitalised label alone. */
function sentenceCase(label: string): string {
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * The "Change:" row of a comparison tooltip, already escaped and coloured.
 *
 * Mirrors the bar label's rule so one hover and one glance at the chart cannot
 * disagree: a real percentage carries its direction's colour, and "nothing to
 * compare against" is shown as ∞ in neutral grey rather than as a win.
 */
function changeLine(current?: number, previous?: number): string {
  if (current == null || !Number.isFinite(current)) return "Change: —";
  if (previous == null || !Number.isFinite(previous) || previous === 0) {
    if (current === 0) return "Change: —";
    return `Change: <span style="color:#666">${current > 0 ? "+" : "-"}∞%</span>`;
  }
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  const rounded = Math.round(pct * 10) / 10;
  const color = rounded >= 0 ? "#2e7d32" : "#c62828";
  const sign = rounded >= 0 ? "+" : "";
  return `Change: <span style="color:${color}">${sign}${escapeHtml(String(rounded))}%</span>`;
}

export function buildBarTooltipFormatter(
  rows: Array<Record<string, unknown>>,
  opts: BarTooltipFormatterOptions,
): (params: any) => string {
  const { formatValue, rowPreviews, comparisonLabel, periodLabel } = opts;
  return (params: any): string => {
    if (!Array.isArray(params) || params.length === 0) return "";

    // Helper series (leading underscore) exist for layout only — the empty
    // column that separates one actual/comparison pair from the next, the
    // std-dev band, the dashed incomplete-period overlay. They carry no value.
    const visible = params.filter(
      (p: any) => !String(p?.seriesName ?? "").startsWith("_"),
    );

    const header = String(params[0]?.axisValueLabel ?? params[0]?.name ?? "");
    const parts = [`<strong>${escapeHtml(header)}</strong>`];

    if (comparisonLabel) {
      // One block per metric: its name, then the two periods and the change.
      // Listing them as two sibling series ("Gross Revenue" and "Gross Revenue
      // (prev. year)") made the reader do the pairing and the subtraction
      // themselves — the tooltip is where that work belongs.
      const suffix = ` (${comparisonLabel})`;
      const order: string[] = [];
      const byMetric = new Map<string, { current?: number; previous?: number; marker: string }>();
      for (const p of visible) {
        const seriesName = String(p?.seriesName ?? "");
        const isComparison = seriesName.endsWith(suffix);
        const metric = isComparison ? seriesName.slice(0, -suffix.length) : seriesName;
        if (!byMetric.has(metric)) {
          byMetric.set(metric, { marker: String(p?.marker ?? "") });
          order.push(metric);
        }
        const entry = byMetric.get(metric)!;
        const value = Number(p?.value);
        if (isComparison) entry.previous = value;
        else {
          entry.current = value;
          // Prefer the actual series' dot — the comparison's is the faded one.
          entry.marker = String(p?.marker ?? entry.marker);
        }
      }

      for (const metric of order) {
        const { current, previous, marker } = byMetric.get(metric)!;
        parts.push(`${marker} <strong>${escapeHtml(metric)}</strong>`);
        parts.push(
          `&nbsp;&nbsp;${escapeHtml(periodLabel ?? "Selected period")}: ` +
            `${escapeHtml(current == null ? "—" : formatValue(current))}`,
        );
        parts.push(
          // Capitalised here only. In a series name it reads as an aside —
          // "Gross Revenue (prev. year)" — but at the start of its own row it
          // sits next to "Last Month" and lowercase looks like a typo.
          `&nbsp;&nbsp;${escapeHtml(sentenceCase(comparisonLabel))}: ` +
            `${escapeHtml(previous == null ? "—" : formatValue(previous))}`,
        );
        parts.push(`&nbsp;&nbsp;${changeLine(current, previous)}`);
      }
    } else {
      for (const p of visible) {
        const seriesName = String(p?.seriesName ?? "");
        const value = formatValue(Number(p?.value));
        // p.marker is ECharts-generated HTML (a colored dot) — safe to inline.
        // seriesName + value are tenant/format controlled → escaped defensively.
        parts.push(`${p?.marker ?? ""} ${escapeHtml(seriesName)}: ${escapeHtml(value)}`);
      }
    }

    let html = parts.join("<br/>");

    // Append the preview when the hovered row maps to one.
    const idx = Number(params[0]?.dataIndex);
    const row = Number.isInteger(idx) ? rows[idx] : undefined;
    html += buildPreviewHtml(resolveRowPreview(row, rowPreviews));
    return html;
  };
}

/**
 * The preview block appended under a bar tooltip's values, as escaped HTML.
 *
 * Returns "" when there is nothing to show — an empty block would give the bar a
 * hover affordance that reveals nothing.
 *
 * The wrapper's `max-width` + `word-break` are load-bearing: an ECharts tooltip
 * sizes to its content, and a text-only ad preview (headline + description) has
 * no natural width, so without them a search-ad preview stretches the tooltip
 * across the whole tile. It stays just wider than the image so the picture,
 * not the copy, sets the tooltip's width.
 */
export function buildPreviewHtml(preview: RowPreview | null): string {
  if (!preview) return "";
  const parts: string[] = [];

  if (preview.kind === "ad") {
    // Advertiser-controlled copy, straight into an innerHTML sink — every one of
    // these MUST stay wrapped in escapeHtml.
    const headline = preview.headlines[0] ?? preview.adName ?? "";
    if (headline) parts.push(`<div><strong>${escapeHtml(headline)}</strong></div>`);
    if (preview.displayUrl) {
      parts.push(`<div style="opacity:0.7">${escapeHtml(preview.displayUrl)}</div>`);
    }
    for (const line of preview.headlines.slice(1)) {
      parts.push(`<div>${escapeHtml(line)}</div>`);
    }
    for (const line of preview.descriptions) {
      parts.push(`<div style="opacity:0.85">${escapeHtml(line)}</div>`);
    }
  }

  const href = previewImageUrl(preview);
  if (href) {
    parts.push(
      `<img src="${escapeHtml(href)}" style="max-width:220px;max-height:220px;display:block;margin-top:6px" loading="lazy" referrerpolicy="no-referrer" />`,
    );
  }

  if (parts.length === 0) return "";
  return `<div style="max-width:260px;white-space:normal;word-break:break-word;margin-top:6px">${parts.join("")}</div>`;
}
